import { ref } from 'vue'
import { clearApiAuthentication, useApi, useSilentApi } from '@/api/useApi'
import API from '@/api/apiUrls'
import { defineStore } from 'pinia'
import type { RouteLocationRaw } from 'vue-router'
import type { UserType } from '@/stores/type/authDjango.type'
import { useSearchUserBaseStore } from '@haskoning/vue-basestore'

const { api } = useApi()
const { api: authenticationProbeApi } = useSilentApi()
const userIndex = API.CORE.USERS.INDEX
const currentUser = API.CORE.USERS.CURRENT_USER

export const useDjangoAuthStore = defineStore('auth', () => {
  const searchUserBaseStore = useSearchUserBaseStore<UserType>(api, userIndex)
  const user = ref<UserType | null>(null)
  const isInitDone = ref(false)
  const loginRedirect = ref<RouteLocationRaw | null>(null)

  async function authenticate({ email, password }: { email: string; password: string }) {
    await api.apiService.authenticationHandler.authenticate(email, password)
    const userResponse = await api.service(currentUser).get()
    user.value = userResponse
    isInitDone.value = true
    return { user: userResponse }
  }

  async function reAuthenticate() {
    try {
      // A 401 is an expected outcome of this authentication probe. Let the
      // router decide whether to show the login page instead of invoking the
      // global expired-session redirect while a navigation is still pending.
      await authenticationProbeApi.apiService.authenticationHandler.setAccessTokenInHeaderFromStore()
      const userResponse = await authenticationProbeApi.service(currentUser).get()

      // The probe may have refreshed the access token, so keep the regular API
      // handler in sync before authenticated stores start loading their data.
      await api.apiService.authenticationHandler.setAccessTokenInHeaderFromStore()
      user.value = userResponse
      return { user: userResponse }
    } catch {
      clearApiAuthentication()
      user.value = null
      return { user: null }
    } finally {
      isInitDone.value = true
    }
  }

  async function logout() {
    await clearApiAuthentication()
    localStorage.removeItem('access')
    localStorage.removeItem('refresh')
    user.value = null
    loginRedirect.value = null
    searchUserBaseStore.resetSearchState()
    isInitDone.value = true
  }

  return {
    ...searchUserBaseStore,
    user,
    isInitDone,
    loginRedirect,
    authenticate,
    reAuthenticate,
    logout
  }
})
